import { Guid } from '@approvalmax/types';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useBillLinkedToAirwallexBatchPayment } from '../../hooks/useBillLinkedToAirwallexBatchPayment';
import { useBillLinkedToAmaxPayXeroBatchPayment } from '../../hooks/useBillLinkedToAmaxPayXeroBatchPayment';
import { useBillLinkedToXeroBatchPayment } from '../../hooks/useBillLinkedToXeroBatchPayment';
import { EditWatchersPopupData } from '../../reducers/page/activePopup/editWatchersPopupReducer';
import { getActivePopup, getActiveRequestSafe } from '../../selectors/pageSelectors';

export const useGetRequest = () => {
    const { requestId } = useSelector(getActivePopup<EditWatchersPopupData>);
    const requestFromRedux = useSelector((state) => selectors.request.findRequestById(state, requestId));

    const { companyId } = useParams<{ companyId: string }>();
    const activeRequest = useSelector(getActiveRequestSafe);
    const airwallexBatchPaymentRequestId =
        activeRequest?.integrationCode === domain.IntegrationCode.XeroAirwallexBatchPayment ? activeRequest.id : '';
    const xeroBatchPaymentRequestId =
        activeRequest?.integrationCode === domain.IntegrationCode.XeroBillBatchPayment ? activeRequest.id : '';
    const amaxPayBatchPaymentRequestId =
        activeRequest?.integrationCode === domain.IntegrationCode.XeroAmaxPayBatchPayment ? activeRequest.id : '';

    const { request: airwallexRequest } = useBillLinkedToAirwallexBatchPayment(
        {
            companyId,
            billId: requestId,
            batchPaymentRequestId: airwallexBatchPaymentRequestId,
        },
        {
            enabled: !requestFromRedux && !!airwallexBatchPaymentRequestId,
        }
    );

    const { request: xeroRequest } = useBillLinkedToXeroBatchPayment(
        {
            path: {
                companyId,
                billRequestId: requestId,
                batchPaymentRequestId: xeroBatchPaymentRequestId,
            },
        },
        { enabled: !requestFromRedux && !!xeroBatchPaymentRequestId }
    );

    const { request: amaxPayRequest } = useBillLinkedToAmaxPayXeroBatchPayment(
        {
            path: {
                companyId,
                billRequestId: requestId,
                batchPaymentRequestId: amaxPayBatchPaymentRequestId,
            },
        },
        {
            enabled: !requestFromRedux && !!amaxPayBatchPaymentRequestId,
        }
    );

    return requestFromRedux || airwallexRequest || xeroRequest || amaxPayRequest;
};

export const useWatchersList = (options: {
    request: selectors.types.ExpandedRequest | null;
    me: selectors.types.ExpandedUser;
    team: selectors.types.ExpandedCompanyUser[];
    search: string;
}) => {
    const { request, me, team, search } = options;

    const allPossibleWatchers = useMemo(() => {
        if (!request || !me) {
            return [];
        }

        const participantEmails = new Set<Guid>();

        request.steps.forEach((step) => {
            step.participants.forEach((participant) => participantEmails.add(participant.userId));
        });

        request.reviewStep?.reviewers.forEach((reviewer) => {
            participantEmails.add(reviewer.userEmail);
        });

        return team.filter(
            (user) =>
                user.id !== request.authorId &&
                !participantEmails.has(user.id || '') &&
                user.status === domain.CompanyUserStatus.Active
        );
    }, [me, request, team]);

    const possibleWatchers = useMemo(() => {
        return allPossibleWatchers.filter((user) => user.displayName.toLowerCase().includes(search.toLowerCase()));
    }, [allPossibleWatchers, search]);

    const hasPossibleWatchers = allPossibleWatchers.length > 0;
    const noWatchersBySearch = hasPossibleWatchers && possibleWatchers.length === 0;

    return {
        allPossibleWatchers,
        possibleWatchers,
        hasPossibleWatchers,
        noWatchersBySearch,
    };
};
